import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import { Button, Grid2 as Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useContext, useState } from 'react';
import Mastercard from '../../../assets/icons/mastercard.svg';
import Visa from '../../../assets/icons/visa.svg';
import { BookingStore } from '../../../utils/stores/BookingStore';
import StripeInput from './StripeInput';
import { commonStyles } from './commonStyles';

// Stripe reports brands with its own identifiers; only the two we accept get an icon.
const brandIcon = (brand: string) => {
    if (brand === 'visa') {
        return <img src={Visa} alt="visa icon" style={{ height: 35 }} />;
    }
    if (brand === 'mastercard') {
        return <img src={Mastercard} alt="mastercard icon" style={{ height: 35 }} />;
    }
    return <CreditCardIcon color="action" />;
};

interface FieldState {
    readonly complete: boolean;
    readonly error?: string;
}

const EMPTY_FIELD: FieldState = { complete: false };

const StripeCardFields = () => {
    const bookingController = useContext(BookingStore);
    const stripe = useStripe();
    const elements = useElements();
    const theme = useTheme();
    const xs = useMediaQuery(theme.breakpoints.only('xs'));

    // Carry over exactly what they typed on the first attempt so the form looks unchanged,
    // falling back to the lead passenger's name. Every field we can save is one less
    // reason to abandon the retry.
    const customer = bookingController?.booking?.customer;
    const [name, setName] = useState(
        bookingController?.payment?.data?.cardDetails?.Name ||
            `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
    );
    const [brand, setBrand] = useState('unknown');
    const [number, setNumber] = useState<FieldState>(EMPTY_FIELD);
    const [expiry, setExpiry] = useState<FieldState>(EMPTY_FIELD);
    const [cvc, setCvc] = useState<FieldState>(EMPTY_FIELD);
    const [nameError, setNameError] = useState<string | undefined>(undefined);
    const [submitError, setSubmitError] = useState<string | undefined>(undefined);
    // The store only starts loading once the card has been tokenised, so without our own
    // guard a second click during tokenisation would authorise the card twice and leave
    // the customer with two holds.
    const [submitting, setSubmitting] = useState(false);

    // Stripe owns the input values, so validity comes from each element's change event
    // rather than from inspecting what the customer typed.
    const handleFieldChange = (setter: (state: FieldState) => void) => (event: any) => {
        setter({ complete: event.complete, error: event.error?.message });
        setSubmitError(undefined);
    };

    // Match the surrounding MUI fields, otherwise the text inside the boxes visibly
    // differs from the rest of the form.
    const elementOptions = {
        style: {
            base: {
                fontSize: '16px',
                color: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                '::placeholder': { color: theme.palette.text.disabled },
            },
            invalid: { color: theme.palette.text.primary },
        },
    };

    // Link is Stripe's own saved-card checkout. It only renders on the card number field,
    // and offering it here would ask the customer to sign up to a third party midway
    // through a payment they have already had declined once.
    const cardNumberOptions = { ...elementOptions, disableLink: true };

    const isReady = !!stripe && !!elements;

    const handleSubmit = async () => {
        const trimmedName = name.trim();
        setNameError(trimmedName ? undefined : 'Card Name is required');
        if (submitting || !isReady || !trimmedName || !number.complete || !expiry.complete || !cvc.complete) {
            return;
        }

        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
            return;
        }

        setSubmitting(true);
        try {
            const result = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: trimmedName,
                    email: customer?.email || undefined,
                    phone: customer?.phone || undefined,
                },
            });

            if (result.error || !result.paymentMethod) {
                setSubmitError(
                    result.error?.message || 'We could not read those card details. Please check and try again.'
                );
                return;
            }

            // Runs the 3DS challenge if Stripe asks for one. The store decides whether the
            // payment is authorised, and only trusts the server's verification.
            const confirmAction = async (clientSecret: string) => {
                const actionResult = await stripe.handleNextAction({ clientSecret });
                if (actionResult.error) {
                    setSubmitError(actionResult.error.message || 'Card authentication failed. Please try again.');
                    return false;
                }
                return true;
            };

            await bookingController.processStripeBooking(result.paymentMethod.id, confirmAction);
        } finally {
            setSubmitting(false);
        }
    };

    const fieldError = (field: FieldState) => !!field.error;

    return (
        <Grid container sx={commonStyles.cardContainer}>
            <Grid container size={{ xs: 12 }} style={{ padding: xs ? '10px 5px' : '0 10px 10px' }}>
                <Typography variant="h6" style={{ color: 'red', paddingBottom: 10, width: '100%' }}>
                    Payment Declined
                </Typography>
                <Typography style={{ fontSize: 16, color: theme.palette.text.soft }}>
                    Your bank declined the payment and no charge has been made. This is often temporary. Please re-enter
                    your card details below and try again, or use a different card.
                </Typography>
            </Grid>
            <Grid
                container
                alignItems="center"
                size={{ xs: 12 }}
                style={{
                    padding: xs ? '10px 5px' : '0 10px 10px',
                    marginBottom: 5,
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 10,
                    width: '100%',
                }}
            >
                <img
                    src={Visa}
                    alt="visa_payment"
                    style={{ height: xs ? 35 : 40, opacity: brand === 'mastercard' ? 0.4 : 1 }}
                />
                <img
                    src={Mastercard}
                    alt="mastercard_payment"
                    style={{ height: xs ? 35 : 40, opacity: brand === 'visa' ? 0.4 : 1 }}
                />
            </Grid>
            <Grid size={{ xs: 12 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                <TextField
                    style={{ width: '100%' }}
                    label="Name on Card"
                    variant="outlined"
                    color="primary"
                    type="text"
                    value={name}
                    error={!!nameError}
                    helperText={nameError}
                    required={true}
                    onChange={(event) => {
                        setName(event.target.value);
                        setNameError(undefined);
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <PersonIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            <Grid size={{ xs: 12 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                <TextField
                    style={{ flex: 'auto', width: '100%' }}
                    label="Card Number"
                    variant="outlined"
                    color="primary"
                    required={true}
                    error={fieldError(number)}
                    helperText={number.error}
                    // Stripe never reports a value to MUI, so the label has to be pinned
                    // or it renders over the card number.
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        inputComponent: StripeInput as any,
                        inputProps: {
                            component: CardNumberElement,
                            options: cardNumberOptions,
                            onChange: (event: any) => {
                                setBrand(event.brand);
                                handleFieldChange(setNumber)(event);
                            },
                        },
                        endAdornment: <InputAdornment position="end">{brandIcon(brand)}</InputAdornment>,
                    }}
                />
            </Grid>
            <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12, sm: 6 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                    <TextField
                        label="Expiry"
                        style={{ width: '100%' }}
                        variant="outlined"
                        color="primary"
                        required={true}
                        error={fieldError(expiry)}
                        helperText={expiry.error}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            inputComponent: StripeInput as any,
                            inputProps: {
                                component: CardExpiryElement,
                                options: elementOptions,
                                onChange: handleFieldChange(setExpiry),
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <CalendarTodayIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} style={{ padding: xs ? '10px 5px' : 10 }}>
                    <TextField
                        label="CVC"
                        style={{ width: '100%' }}
                        variant="outlined"
                        color="primary"
                        required={true}
                        error={fieldError(cvc)}
                        helperText={cvc.error}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            inputComponent: StripeInput as any,
                            inputProps: {
                                component: CardCvcElement,
                                options: elementOptions,
                                onChange: handleFieldChange(setCvc),
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <LockIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
            </Grid>
            {submitError && (
                <Grid size={{ xs: 12 }} style={{ padding: xs ? '0 5px' : '0 10px' }}>
                    <Typography style={{ color: 'red', fontSize: 15 }}>{submitError}</Typography>
                </Grid>
            )}
            <Grid container size={{ xs: 12 }} alignItems="center" style={{ padding: xs ? '10px 5px' : 10 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Button
                        sx={commonStyles.bookingButton}
                        variant="contained"
                        disabled={submitting || bookingController.isLoading || !isReady}
                        onClick={handleSubmit}
                    >
                        {submitting || bookingController.isLoading ? (
                            <CircularProgress variant="indeterminate" disableShrink size={24} thickness={4} />
                        ) : (
                            'Try Payment Again'
                        )}
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} sx={commonStyles.encryptSect}>
                    <LockIcon style={{ color: 'rgb(0 168 0)', margin: '0 10px 0 15px' }} />
                    <Typography style={{ color: 'rgb(0 168 0)', padding: '14px 0', marginRight: 15 }}>
                        256 Bit Encryption
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default StripeCardFields;
